import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { CheckCircle, Loader } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function UpgradeSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { authToken } = useAuth();
  const [status, setStatus] = useState('checking');
  const [plan, setPlan] = useState('');

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      navigate('/pricing');
      return;
    }

    const checkPaymentStatus = async (attempt = 0) => {
      try {
        const response = await axios.get(`${API}/subscriptions/checkout-status/${sessionId}`, {
          headers: { Authorization: `Bearer ${authToken}` }
        });

        if (response.data.payment_status === 'paid') {
          setStatus('success');
          setPlan(response.data.plan);
        } else if (response.data.status === 'expired') {
          setStatus('expired');
        } else if (attempt < 5) {
          // Poll again after 2 seconds
          setTimeout(() => checkPaymentStatus(attempt + 1), 2000);
        } else {
          setStatus('timeout');
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        setStatus('error');
      }
    };

    checkPaymentStatus();
  }, [searchParams, authToken, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
        {status === 'checking' && (
          <>
            <Loader className="h-16 w-16 text-emerald-600 mx-auto mb-4 animate-spin" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Processing Payment...</h2>
            <p className="text-gray-600">Please wait while we confirm your upgrade</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="h-16 w-16 text-emerald-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Upgrade Successful!</h2>
            <p className="text-gray-600 mb-6">
              Your plan has been upgraded to <span className="font-semibold text-emerald-600 capitalize">{plan}</span>.
              You now have access to all {plan} features.
            </p>
            <button
              onClick={() => navigate('/')}
              className="w-full bg-emerald-600 text-white py-3 px-6 rounded-lg hover:bg-emerald-700 font-semibold transition-colors"
            >
              Go to Dashboard
            </button>
          </>
        )}

        {status === 'expired' && (
          <>
            <div className="h-16 w-16 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">⚠️</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Session Expired</h2>
            <p className="text-gray-600 mb-6">Your payment session has expired. Please try again.</p>
            <button
              onClick={() => navigate('/pricing')}
              className="w-full bg-emerald-600 text-white py-3 px-6 rounded-lg hover:bg-emerald-700 font-semibold transition-colors"
            >
              Back to Pricing
            </button>
          </>
        )}

        {(status === 'error' || status === 'timeout') && (
          <>
            <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">❌</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Verification Failed</h2>
            <p className="text-gray-600 mb-6">
              We couldn't verify your payment. Please contact support if you were charged.
            </p>
            <button
              onClick={() => navigate('/pricing')}
              className="w-full bg-emerald-600 text-white py-3 px-6 rounded-lg hover:bg-emerald-700 font-semibold transition-colors"
            >
              Back to Pricing
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default UpgradeSuccess;